// src/components/NavigatorReassignPanel.jsx
//
// "A Nav should be able to reassign a patient that is assigned to one Nav
// to any other Nav." Wired into PatientDetailPage.js's Overview tab, inside
// the Navigator Info card. Every reassignment is written to the patient's
// activity trail as NAVIGATOR_REASSIGNED (PatientActivityTrail.jsx).
//
// A plain dropdown, not a search picker like SubscriberSearchPicker.jsx —
// the navigator roster is staff-sized, not a set that grows into the
// hundreds the way patients/subscribers do, and GET /navigators/assignable
// returns the whole list in one call rather than being search-backed.

import { useState } from 'react';
import { navigatorAPI, patientNavigatorAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

export default function NavigatorReassignPanel({ patientId, currentNavigatorName, onReassigned }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleOpen() {
    setOpen(true);
    setError('');
    setLoadingOptions(true);
    try {
      const res = await navigatorAPI.assignable();
      setOptions(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Could not load navigators.');
    } finally {
      setLoadingOptions(false);
    }
  }

  async function handleConfirm() {
    if (!selectedId) return;
    setSubmitting(true);
    setError('');
    try {
      await patientNavigatorAPI.reassign(patientId, selectedId);
      const picked = options.find(o => o.id === selectedId);
      setOpen(false);
      setSelectedId('');
      toast.success(picked ? `Reassigned to ${picked.name}.` : 'Reassigned.');
      onReassigned?.(picked?.name);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error
        || 'Could not reassign this patient.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12, padding: '5px 12px' }}
        onClick={handleOpen}>
        {currentNavigatorName ? 'Reassign navigator' : 'Assign a navigator'}
      </button>
    );
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <select
        className="form-select"
        value={selectedId}
        disabled={loadingOptions || submitting}
        onChange={(e) => setSelectedId(e.target.value)}
        style={{ fontSize: 13 }}
      >
        <option value="">{loadingOptions ? 'Loading navigators…' : 'Select a navigator…'}</option>
        {options.map(o => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-accent" style={{ fontSize: 12, padding: '5px 12px' }}
          disabled={!selectedId || submitting} onClick={handleConfirm}>
          {submitting ? 'Reassigning…' : 'Confirm'}
        </button>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}
          disabled={submitting}
          onClick={() => { setOpen(false); setSelectedId(''); setError(''); }}>
          Cancel
        </button>
      </div>
      {error && <div style={{ color: 'var(--red)', fontSize: 12 }}>{error}</div>}
    </div>
  );
}
